import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Users } from 'lucide-react';
import { useTranslation } from '@/context/TranslationContext';
import { BACKEND_URL } from '@/lib/backendUrl';
import { MilestoneCelebration } from '@/components/landing/MilestoneCelebration';
import { MemberAvatars } from '@/components/landing/MemberAvatars';

const cache = {};

/** One fetch per page load (shared by hero + footer). `?celebrate=100` previews a milestone — admins only, enforced server-side. */
export function useMemberStats() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    const preview = new URLSearchParams(window.location.search).get('celebrate');
    const key = preview || 'live';
    cache[key] = cache[key] || axios.get(`${BACKEND_URL}/api/public/stats`, { params: preview ? { preview_milestone: preview } : {} }).then((r) => r.data);
    cache[key].then(setStats).catch((e) => { delete cache[key]; console.warn('MemberCounter: stats unavailable', e); });
  }, []);
  return stats;
}

function useCountUp(target, duration = 1400) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!target) return undefined;
    let frame;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      setValue(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, duration]);
  return value;
}

export function LiveDot() {
  return (
    <span className="relative flex w-2 h-2 shrink-0" aria-hidden="true">
      <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-60 animate-ping" />
      <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-400" />
    </span>
  );
}

/** Hero pill: real member count from the database, plus the milestone celebration when one was just crossed. */
export function MemberCounter() {
  const { t } = useTranslation();
  const stats = useMemberStats();
  const members = Number.isFinite(stats?.members) ? stats.members : null;
  const shown = useCountUp(members || 0);
  if (members === null) return null;
  return (
    <div className="mt-6 flex flex-col items-start gap-3">
      <div className="inline-flex flex-wrap items-center gap-3 pl-3 pr-4 py-2 rounded-full border border-[var(--border)] bg-[var(--bg-surface)]" data-testid="member-counter" aria-live="polite">
        <LiveDot />
        <MemberAvatars />
        <Users className="w-4 h-4 shrink-0" style={{ color: 'var(--accent)' }} aria-hidden="true" />
        <p className="text-sm text-[var(--text-secondary)]">
          <span className="font-semibold tabular-nums text-[var(--text-primary)]" data-testid="member-counter-value">{shown.toLocaleString()}</span>{' '}
          {members === 1 ? t('person has joined so far.') : t('people have joined so far.')}{' '}
          <span className="text-[var(--text-secondary)]/70">{t('Live count from our database.')}</span>
        </p>
      </div>
      {stats?.milestone?.celebrating && <MilestoneCelebration milestone={stats.milestone} />}
    </div>
  );
}

/** Compact inline version for footers. */
export function MemberCounterInline() {
  const { t } = useTranslation();
  const stats = useMemberStats();
  const members = Number.isFinite(stats?.members) ? stats.members : null;
  if (members === null) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="footer-member-counter">
      <span className="inline-flex items-center gap-2 text-xs text-[var(--text-secondary)]">
        <LiveDot />
        <MemberAvatars size="sm" />
        <span className="font-semibold tabular-nums text-[var(--text-primary)]" data-testid="footer-member-counter-value">{members.toLocaleString()}</span>
        {members === 1 ? t('member so far') : t('members so far')} · {t('live count')}
      </span>
      {stats?.milestone?.celebrating && <MilestoneCelebration milestone={stats.milestone} compact />}
    </div>
  );
}
